import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { CheckCircle, FileSearch, Brain, AlertCircle, RefreshCw, FileText, Cpu } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// 海运提单卡片组件
function BillOfLadingCard({
  isVisible,
  isScanning,
  showHighlights,
  scanProgress,
  isComplete
}: {
  isVisible: boolean;
  isScanning: boolean;
  showHighlights: boolean;
  scanProgress: number;
  isComplete: boolean;
}) {
  const { t } = useLanguage();
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: -60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 60, opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`absolute left-1 top-1 w-[calc(100%-8px)] h-[calc(100%-8px)] rounded-lg overflow-hidden shadow-lg ${isComplete ? 'bg-emerald-900/40 border border-emerald-500/30' : 'bg-slate-800/90 border border-slate-600/50'}`}
        >
          {/* 单据内容 */}
          <div className={`p-1.5 text-[5px] space-y-0.5 ${isComplete ? 'text-emerald-300/80' : 'text-slate-400'}`}>
            <div className={`font-bold text-[6px] ${isComplete ? 'text-emerald-200' : 'text-slate-300'}`}>B/L</div>
            <div className={`h-px my-0.5 ${isComplete ? 'bg-emerald-500/30' : 'bg-slate-600/50'}`} />
            <motion.div
              className={`${showHighlights ? (isComplete ? 'bg-emerald-500/30 border border-emerald-400/50 rounded px-0.5' : 'bg-violet-500/30 border border-violet-400/50 rounded px-0.5') : ''}`}
            >
              {t("docAnim.productName")}
            </motion.div>
            <motion.div
              className={`${showHighlights ? (isComplete ? 'bg-emerald-500/30 border border-emerald-400/50 rounded px-0.5' : 'bg-violet-500/30 border border-violet-400/50 rounded px-0.5') : ''}`}
            >
              {t("docAnim.hsCode")}
            </motion.div>
            <div>{t("docAnim.weight")}</div>
            <div>{t("docAnim.pieces")}</div>
          </div>

          {/* 扫描光带 */}
          <AnimatePresence>
            {isScanning && (
              <motion.div
                initial={{ top: 0 }}
                animate={{ top: "100%" }}
                transition={{ duration: 1.5, ease: "linear" }}
                className="absolute left-0 right-0 h-1 bg-gradient-to-b from-violet-400/80 via-violet-500/60 to-transparent"
                style={{ boxShadow: "0 0 10px rgba(139, 92, 246, 0.6)" }}
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// 模板缩略图组件
function TemplateGallery({ isActive, isMerging, showFinal, isComplete }: { isActive: boolean; isMerging: boolean; showFinal: boolean; isComplete: boolean }) {
  const { t } = useLanguage();
  const templates = [
    { type: t("docAnim.templateBl"), color: "from-blue-500/20 to-blue-600/20" },
    { type: t("docAnim.templateInvoice"), color: "from-green-500/20 to-green-600/20" },
    { type: t("docAnim.templatePacking"), color: "from-orange-500/20 to-orange-600/20" },
    { type: t("docAnim.templateCustoms"), color: "from-purple-500/20 to-purple-600/20" },
    { type: t("docAnim.templateOrigin"), color: "from-pink-500/20 to-pink-600/20" },
  ];

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute inset-1 flex flex-wrap justify-center items-center gap-0.5 p-1"
        >
          {!showFinal && templates.map((template, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, rotate: (index - 2) * 15 }}
              animate={{
                opacity: isMerging ? [1, 0] : 1,
                rotate: isMerging ? 0 : (index - 2) * 8,
                scale: isMerging ? [1, 0.5, 0] : 1,
                x: isMerging ? 0 : 0,
                y: isMerging ? 0 : 0,
              }}
              transition={{
                duration: 0.5,
                delay: isMerging ? index * 0.1 : index * 0.1,
                ease: "easeInOut"
              }}
              className={`w-5 h-6 rounded bg-gradient-to-br ${template.color} border border-slate-500/30 flex items-center justify-center`}
            >
              <span className="text-[4px] text-slate-300">{template.type}</span>
            </motion.div>
          ))}

          {/* 合并后的统一模板 */}
          {(isMerging || showFinal) && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: isMerging ? 0.5 : 0, duration: 0.3 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center ${isComplete ? 'bg-gradient-to-br from-emerald-500/30 to-emerald-600/30 border-emerald-400/50' : 'bg-gradient-to-br from-violet-500/30 to-purple-600/30 border-violet-400/50'}`}>
                <FileText className={`w-4 h-4 ${isComplete ? 'text-emerald-400' : 'text-violet-400'}`} />
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// 异常检测卡片
function AnomalyCard({ isActive, isFixed, isComplete }: { isActive: boolean; isFixed: boolean; isComplete: boolean }) {
  const { t } = useLanguage();
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className={`absolute inset-1 rounded-lg p-1.5 overflow-hidden ${isComplete ? 'bg-emerald-900/40 border border-emerald-500/30' : 'bg-slate-800/80 border border-slate-600/50'}`}
        >
          <div className={`text-[5px] space-y-0.5 ${isComplete ? 'text-emerald-300/80' : 'text-slate-400'}`}>
            <div className={`font-bold text-[6px] ${isComplete ? 'text-emerald-200' : 'text-slate-300'}`}>{t("docAnim.docCheck")}</div>
            <div className={`h-px my-0.5 ${isComplete ? 'bg-emerald-500/30' : 'bg-slate-600/50'}`} />
            <div className="flex items-center gap-1">
              <span>{t("docAnim.packingQty")}</span>
              <motion.span
                animate={{
                  color: isFixed ? "#4ade80" : "#f87171",
                  scale: isFixed ? [1, 1.2, 1] : 1
                }}
                className="font-bold"
              >
                {isFixed ? t("docAnim.correct") : t("docAnim.incorrect")}
              </motion.span>
            </div>
          </div>

          {/* 异常标记和机器人修复动效 */}
          {!isFixed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute top-3 right-1"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className="w-2 h-2 rounded-full bg-red-500/80"
              />
            </motion.div>
          )}

          {/* 机器人修复剪影 */}
          <AnimatePresence>
            {isActive && !isFixed && (
              <motion.div
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 8, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute top-2 right-0"
              >
                <Cpu className={`w-3 h-3 ${isComplete ? 'text-emerald-400' : 'text-violet-400'}`} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// FMS进度条组件
function FMSProgress({ isActive, isComplete }: { isActive: boolean; isComplete: boolean }) {
  const { t } = useLanguage();
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-1 flex flex-col items-center justify-center"
        >
          <AnimatePresence mode="wait">
            {!isComplete ? (
              <motion.div
                key="syncing"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4, scale: 0.96 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center"
              >
                <div className="text-[6px] text-slate-300 font-bold mb-1">{t("docAnim.fmsSystem")}</div>
                <div className="w-12 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-violet-500 to-emerald-400 rounded-full"
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="complete"
                initial={{ opacity: 0, y: 6, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="flex flex-col items-center gap-1"
              >
                <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="px-1.5 py-0.5 bg-emerald-500/20 border border-emerald-400/30 rounded text-[5px] text-emerald-400">
                  {t("docAnim.syncedToFms")}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// 流程模块组件
function ProcessModule({
  icon: Icon,
  title,
  isActive,
  isComplete,
  bubbleText,
  showBubble,
  children,
  showBrainFlicker,
}: {
  icon: React.ElementType;
  title: string;
  isActive: boolean;
  isComplete: boolean;
  bubbleText: string;
  showBubble: boolean;
  children?: React.ReactNode;
  showBrainFlicker?: boolean;
}) {
  return (
    <motion.div
      animate={{ 
        scale: isActive ? 1.02 : 1,
        borderColor: isComplete ? "rgba(74, 222, 128, 0.6)" : isActive ? "rgba(139, 92, 246, 0.8)" : "rgba(71, 85, 105, 0.3)"
      }}
      transition={{ duration: 0.3 }}
      className={`relative h-28 rounded-xl border-2 transition-all duration-300 overflow-hidden ${isComplete ? 'bg-emerald-900/20' : 'bg-slate-800/60'}`}
    >
      {/* 激活状态光晕 */}
      {isActive && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          className="absolute inset-0 rounded-xl bg-violet-500/10 pointer-events-none" 
        />
      )}
      
      {/* 完成状态光晕 */}
      {isComplete && (
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          className="absolute inset-0 rounded-xl bg-emerald-500/10 pointer-events-none" 
        />
      )}
      
      {/* 顶部标题栏 */}
      <div className={`flex items-center gap-1.5 px-2 py-1.5 border-b ${isComplete ? 'border-emerald-500/30' : isActive ? 'border-violet-500/30' : 'border-slate-700/50'}`}>
        <motion.div
          animate={showBrainFlicker ? { opacity: [1, 0.5, 1, 0.5, 1] } : {}}
          transition={{ duration: 0.8 }}
          className={`w-5 h-5 rounded flex items-center justify-center ${isComplete ? "bg-emerald-500/30" : isActive ? "bg-violet-500/30" : "bg-slate-700/50"}`}
        >
          <Icon className={`w-3 h-3 ${isComplete ? "text-emerald-400" : isActive ? "text-violet-400" : "text-slate-400"}`} />
        </motion.div>
        <span className={`text-[8px] font-medium leading-tight ${isComplete ? "text-emerald-300" : isActive ? "text-violet-300" : "text-slate-300"}`}>
          {title}
        </span>
        
        {/* 完成标记 */}
        <AnimatePresence>
          {isComplete && (
            <motion.div 
              initial={{ scale: 0, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0, opacity: 0 }} 
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="ml-auto"
            >
              <CheckCircle className="w-3 h-3 text-emerald-400" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* 内容区域 */}
      <div className="relative h-[calc(100%-28px)]">
        {children}
      </div>
      
      {/* 提示气泡 */}
      <AnimatePresence>
        {showBubble && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap z-20"
          >
            <div className="px-2 py-1 text-[8px] font-medium text-violet-300 bg-violet-500/20 rounded-lg border border-violet-400/30 backdrop-blur-xs">
              {bubbleText}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-violet-500/20 border-r border-b border-violet-400/30 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// 链路连接线组件
function ConnectionPath({ 
  isActive, 
  showFlowingLight,
  direction = "horizontal"
}: { 
  isActive: boolean; 
  showFlowingLight: boolean;
  direction?: "horizontal" | "vertical";
}) {
  const isHorizontal = direction === "horizontal";
  
  return (
    <div className={`relative flex items-center justify-center ${isHorizontal ? 'w-4 h-full' : 'h-4 w-full'}`}>
      {/* 虚线链路 */}
      <div 
        className={`${isHorizontal ? 'w-full h-0.5' : 'h-full w-0.5'} ${isActive ? 'bg-violet-400/60' : 'bg-slate-600/40'}`}
        style={{
          background: isActive 
            ? 'linear-gradient(90deg, rgba(139, 92, 246, 0.6), rgba(167, 139, 250, 0.6))' 
            : 'repeating-linear-gradient(90deg, rgba(100, 116, 139, 0.4) 0px, rgba(100, 116, 139, 0.4) 4px, transparent 4px, transparent 8px)'
        }}
      />
      
      {/* 流动光点 */}
      <AnimatePresence>
        {showFlowingLight && (
          <motion.div
            initial={isHorizontal ? { left: 0 } : { top: 0 }}
            animate={isHorizontal ? { left: "100%" } : { top: "100%" }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute"
          >
            <motion.div 
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.3, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-violet-400"
              style={{ 
                boxShadow: "0 0 8px rgba(139, 92, 246, 0.8), 0 0 16px rgba(139, 92, 246, 0.4)",
                transform: "translate(-50%, -50%)"
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// 识别准确率数字跳动组件
function AccuracyCounter({ isActive, isComplete }: { isActive: boolean; isComplete: boolean }) {
  const { t } = useLanguage();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isActive) {
      const target = 99.2;
      const duration = 800;
      const steps = 30;
      const increment = target / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current * 10) / 10);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setCount(0);
    }
    return undefined;
  }, [isActive]);

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className={`absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[7px] ${isComplete ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-300' : 'bg-violet-500/20 border border-violet-400/30 text-violet-300'}`}
        >
          {t("docAnim.accuracy")} <span className={`font-bold ${isComplete ? 'text-emerald-400' : 'text-violet-400'}`}>{count}%</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function DocumentEmployeeCard() {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);
  const [finalFlash, setFinalFlash] = useState(false);

  // 动画阶段:
  // 0: 初始静态
  // 1: 单据滑入OCR模块
  // 2: 扫描中 + LLM图标闪烁
  // 3: 高亮显示 + 准确率跳动 + 光点流向模板学习
  // 4: 模板展示
  // 5: 模板融合 + 气泡提示 + 光流流向异常检测
  // 6: 异常检测激活
  // 7: 异常修复 + 光带流向FMS
  // 8: FMS进度条
  // 9: FMS完成 + 整体闪烁
  // 10: 重置

  useEffect(() => {
    const sequence = [
      { phase: 0, duration: 1000 },
      { phase: 1, duration: 800 },
      { phase: 2, duration: 1500 },
      { phase: 3, duration: 1200 },
      { phase: 4, duration: 1000 },
      { phase: 5, duration: 1200 },
      { phase: 6, duration: 800 },
      { phase: 7, duration: 1200 },
      { phase: 8, duration: 1000 },
      { phase: 9, duration: 5000 },
      { phase: 10, duration: 500 },
    ];
    
    let currentIndex = 0;
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const runSequence = () => {
      const current = sequence[currentIndex]!;
      setPhase(current.phase);
      
      if (current.phase === 9) {
        setFinalFlash(true);
        setTimeout(() => setFinalFlash(false), 600);
      }
      
      if (current.phase === 10) {
        setPhase(0);
      }
      
      currentIndex = (currentIndex + 1) % sequence.length;
      timeoutId = setTimeout(runSequence, current.duration);
    };
    
    timeoutId = setTimeout(runSequence, 500);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="relative h-full rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 overflow-hidden flex flex-col">
      {/* 背景网格 */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }} />
      </div>
      
      {/* 顶部光晕 */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-violet-500/10 blur-3xl rounded-full" />
      
      {/* 最终闪烁效果 */}
      <AnimatePresence>
        {finalFlash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 bg-violet-400/20 z-30 pointer-events-none"
          />
        )}
      </AnimatePresence>
      
      {/* 标题栏 */}
      <div className="relative z-10 p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-violet-400 animate-pulse" />
          <h3 className="text-base font-semibold text-slate-100">{t("documentWorker.title")}</h3>
        </div>
      </div>
      
      {/* 主内容区 - 四模块流程布局 */}
      <div className="relative z-10 p-4 flex-1">
        <div className="h-full flex flex-col gap-2">
          {/* 第一行: OCR识别 + 模板自学习 */}
          <div className="flex items-stretch gap-2 flex-1">
            {/* OCR + LLM 智能识别模块 */}
            <div className="flex-1">
              <ProcessModule
                icon={FileSearch}
                title={t("documentWorker.h1")}
                isActive={phase >= 1 && phase <= 3}
                isComplete={phase >= 4}
                bubbleText={t("docAnim.recognizing")}
                showBubble={phase === 2}
                showBrainFlicker={phase === 2}
              >
                <BillOfLadingCard
                  isVisible={phase >= 1}
                  isScanning={phase === 2}
                  showHighlights={phase >= 3}
                  scanProgress={phase === 2 ? 100 : 0}
                  isComplete={phase >= 4}
                />
                <AccuracyCounter isActive={phase >= 3} isComplete={phase >= 4} />
              </ProcessModule>
            </div>
            
            {/* 模板自学习模块 */}
            <div className="flex-1">
              <ProcessModule
                icon={Brain}
                title={t("documentWorker.h2")}
                isActive={phase >= 4 && phase <= 5}
                isComplete={phase >= 6}
                bubbleText={t("docAnim.newTemplates")}
                showBubble={phase === 5}
              >
                <TemplateGallery isActive={phase >= 4} isMerging={phase === 5} showFinal={phase >= 6} isComplete={phase >= 6} />
              </ProcessModule>
            </div>
          </div>
          
          {/* 第二行: 异常检测 + FMS同步 */}
          <div className="flex items-stretch gap-2 flex-1">
            {/* 异常检测模块 */}
            <div className="flex-1">
              <ProcessModule
                icon={AlertCircle}
                title={t("documentWorker.h3")}
                isActive={phase >= 6 && phase <= 7}
                isComplete={phase >= 8}
                bubbleText={t("docAnim.anomalyFixed")}
                showBubble={phase === 7}
              >
                <AnomalyCard isActive={phase >= 6} isFixed={phase >= 7} isComplete={phase >= 8} />
              </ProcessModule>
            </div>
            
            {/* FMS 一键同步模块 */}
            <div className="flex-1">
              <ProcessModule
                icon={RefreshCw}
                title={t("documentWorker.h4")}
                isActive={phase >= 8 && phase <= 9}
                isComplete={phase >= 9}
                bubbleText={t("docAnim.syncing")}
                showBubble={phase === 8}
              >
                 <FMSProgress isActive={phase >= 8} isComplete={phase >= 9} />
              </ProcessModule>
            </div>
          </div>
        </div>
      </div>
      
      {/* 底部渐变 */}
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-900 to-transparent pointer-events-none" />
    </div>
  );
}
